/* ==========================================================================
   ogai-product.js — product page behaviour.

   Loaded deferred from sections/product-details.liquid.

   Six jobs, all of them closing gaps rather than adding decoration:

   1. content-scrollspy.js builds an in-page nav from every .accordion__title
      it finds, but scrollToSection() does NOT open a collapsed <details>
      before scrolling to it. Once the product description is split into
      accordions, that means every nav click lands on a collapsed row. Fixed
      here rather than by editing content-scrollspy.js, which is vendor code
      shared with other templates.

   2. That same file reads no location.hash on load, so a link to a section
      inside a collapsed row goes nowhere useful.

   3. The bundle detail rows are bound to a variant option value, so only the
      bundle the buyer actually selected is shown. Liquid renders the correct
      initial state; this keeps it in step as they change selection.

   4. Power kits on the Core page act on the cart as soon as one is chosen,
      one kit or none (see initPowerKits below).

   5. The package cards drive the hidden Knowledge Bundle buttons, and
   6. "Compare devices" stays open across a variant change.

   Everything is null-guarded — on a product with no accordions, no scrollspy
   and no bound rows, this does nothing at all.
   ========================================================================== */

(function () {
  'use strict';

  var HEADER_ALLOWANCE = 20;

  function onReady(fn) {
    if (document.readyState !== 'loading') fn();
    else document.addEventListener('DOMContentLoaded', fn);
  }

  /* Height of anything fixed/sticky at the top, so we do not scroll a heading
     underneath the header. Mirrors the selector list content-scrollspy.js
     uses, so the two agree on where the top of the page is. */
  function stickyOffset() {
    var selectors = ['header', '.header', '.sticky-header', '.fixed-header', 'content-scrollspy'];
    var total = 0;

    for (var i = 0; i < selectors.length; i++) {
      var el = document.querySelector(selectors[i]);
      if (!el) continue;
      var pos = window.getComputedStyle(el).position;
      if (pos !== 'fixed' && pos !== 'sticky') continue;
      total += el.getBoundingClientRect().height;
    }

    return total;
  }

  /* Open every collapsed <details> above this element and report whether
     anything actually had to open — the caller needs to know so it can wait
     for the accordion's 400ms height animation before measuring. */
  function revealAncestors(el) {
    var opened = false;
    var node = el;

    while (node && node !== document.body) {
      if (node.tagName === 'DETAILS' && !node.open) {
        node.open = true;
        opened = true;
      }
      node = node.parentElement;
    }

    return opened;
  }

  function scrollToElement(el) {
    var top = window.pageYOffset + el.getBoundingClientRect().top;
    window.scrollTo({
      top: Math.max(0, top - stickyOffset() - HEADER_ALLOWANCE),
      behavior: 'smooth',
    });
  }

  function targetFromHash(hash) {
    if (!hash || hash === '#') return null;
    var id = decodeURIComponent(hash.slice(1));
    if (!id) return null;
    try {
      return document.getElementById(id);
    } catch (e) {
      return null;
    }
  }

  /* ---- scrollspy: open the row before jumping to it --------------------- */

  function initScrollspyReveal() {
    document.addEventListener(
      'click',
      function (event) {
        var link = event.target.closest && event.target.closest('[data-scrollspy-link]');
        if (!link) return;

        var target = targetFromHash(link.getAttribute('href') || '');
        if (!target) return;

        if (!revealAncestors(target)) return;

        // The row was closed. content-scrollspy has already measured and
        // started its own scroll against the collapsed height, so re-scroll
        // once the accordion has finished expanding (400ms in global.js).
        window.setTimeout(function () {
          scrollToElement(target);
        }, 420);
      },
      true
    );
  }

  /* ---- deep links ------------------------------------------------------- */

  function initHashReveal() {
    function handle() {
      var target = targetFromHash(window.location.hash);
      if (!target) return;
      var opened = revealAncestors(target);
      window.setTimeout(function () { scrollToElement(target); }, opened ? 420 : 60);
    }

    if (window.location.hash) window.setTimeout(handle, 300);
    window.addEventListener('hashchange', handle);
  }

  /* ---- keep the scrollspy nav honest ------------------------------------ */
  /* content-scrollspy builds its menu once, from every .accordion__title on
     the page, and has no idea that three of the four bundle rows are hidden.
     Left alone it offers four bundle links, three of which scroll to nothing.
     This hides any nav item whose target is not currently rendered. */

  function syncScrollspyVisibility() {
    var links = document.querySelectorAll('[data-scrollspy-link]');

    for (var i = 0; i < links.length; i++) {
      var target = targetFromHash(links[i].getAttribute('href') || '');
      var buried = false;

      for (var node = target; node && node !== document.body; node = node.parentElement) {
        if (node.hidden) { buried = true; break; }
      }

      links[i].hidden = !target || buried;
    }
  }

  function initScrollspySync() {
    var nav = document.querySelector('[data-scrollspy-menu]');
    if (!nav) return;

    // The menu is generated asynchronously, and regenerated on resize, so
    // watch for it rather than guessing a delay.
    if ('MutationObserver' in window) {
      new MutationObserver(syncScrollspyVisibility).observe(nav, { childList: true });
    }

    window.setTimeout(syncScrollspyVisibility, 400);
    window.setTimeout(syncScrollspyVisibility, 1200);
  }

  /* ---- bundle rows follow the variant picker ---------------------------- */

  function initVariantBoundRows() {
    var rows = document.querySelectorAll('[data-ogai-option-value]');
    if (!rows.length) return;

    function apply(variant) {
      if (!variant || !variant.options) return;

      for (var i = 0; i < rows.length; i++) {
        var wanted = rows[i].getAttribute('data-ogai-option-value');
        // Exact match, never "contains": the word "Core" appears inside every
        // Knowledge Bundle value, so a substring test shows all four at once.
        var show = variant.options.indexOf(wanted) !== -1;
        rows[i].hidden = !show;

        // Collapse a row on its way out, so it does not reappear pre-opened.
        if (!show) {
          var details = rows[i].querySelector('details');
          if (details) details.open = false;
        }
      }

      syncScrollspyVisibility();
    }

    if (typeof subscribe === 'function' && typeof PUB_SUB_EVENTS !== 'undefined') {
      subscribe(PUB_SUB_EVENTS.variantChange, function (event) {
        if (event && event.data && event.data.variant) apply(event.data.variant);
      });
    }
  }

  /* ---- device comparison highlight -------------------------------------- */
  /* product-info.js re-renders .custom__liquid on variant change, which would
     normally be enough — but it only does so for blocks carrying an id, and
     the re-render races the event. Setting the class directly is cheap and
     makes the highlight correct either way. */

  function initDeviceCompare() {
    function apply(variant) {
      var wrap = document.querySelector('[data-ogai-device-compare]');
      if (!wrap || !variant || !variant.options) return;

      var device = variant.options[0] || '';
      var cols = wrap.querySelectorAll('[data-ogai-device]');

      for (var i = 0; i < cols.length; i++) {
        var key = cols[i].getAttribute('data-ogai-device');
        cols[i].classList.toggle('is-selected', device.indexOf(key) !== -1);
      }
    }

    if (typeof subscribe === 'function' && typeof PUB_SUB_EVENTS !== 'undefined') {
      subscribe(PUB_SUB_EVENTS.variantChange, function (event) {
        if (event && event.data && event.data.variant) apply(event.data.variant);
      });
    }
  }

  /* ---- power kits on the Core page -------------------------------------- */
  /* snippets/ogai-power-addons.liquid renders the kits as a radio group, one
     or none. Choosing acts on the cart at once:

       none -> kit      add the kit's variants, open the drawer
       kit A -> kit B   remove one unit of each A-only variant, add each
                        B-only variant; shared variants are left alone
       kit -> none      remove one unit of each of the kit's variants

     Removing one unit (never zeroing the line) means a power bank the buyer
     added some other way survives a change of kit. The selection is derived
     from the cart, not remembered: on load, after a variant change re-renders
     the block (product-info.js replaces .custom__liquid innerHTML), and after
     any cart change made elsewhere (drawer quantity, remove). A kit counts as
     selected when all its variants are in the cart; when two kits qualify,
     the larger one wins, since it contains the smaller. */

  function initPowerKits() {
    var first = document.querySelector('[data-ogai-kits]');
    if (!first) return;
    var wrap = first.closest('.custom__liquid') || first.parentNode;
    var miniCart = document.querySelector('mini-cart');
    var busy = false;

    function group() { return wrap.querySelector('[data-ogai-kits]'); }
    function radios() { return wrap.querySelectorAll('[data-ogai-kits] input[type="radio"]'); }
    function idsOf(input) { return input && input.value ? input.value.split(',') : []; }

    function selected() {
      var r = radios();
      for (var i = 0; i < r.length; i++) if (r[i].checked) return r[i];
      return null;
    }

    function say(text) {
      var el = wrap.querySelector('.ogai-addons__status');
      if (el) el.textContent = text;
    }

    function setBusy(on) {
      busy = on;
      var g = group();
      if (g) g.setAttribute('aria-busy', on ? 'true' : 'false');
      var r = radios();
      for (var i = 0; i < r.length; i++) r[i].disabled = on;
    }

    function qtyMap(cart) {
      var q = {};
      (cart.items || []).forEach(function (item) {
        q[String(item.variant_id)] = (q[String(item.variant_id)] || 0) + item.quantity;
      });
      return q;
    }

    function syncFrom(cart) {
      var q = qtyMap(cart);
      var r = radios();
      var best = null;
      var bestSize = 0;
      for (var i = 0; i < r.length; i++) {
        var ids = idsOf(r[i]);
        if (!ids.length) continue;
        var all = ids.every(function (id) { return q[id] > 0; });
        if (all && ids.length > bestSize) { best = r[i]; bestSize = ids.length; }
      }
      for (var j = 0; j < r.length; j++) {
        r[j].checked = best ? r[j] === best : !r[j].value;
      }
      wrap._ogaiCurrent = best ? best.value : '';
    }

    function syncFromServer() {
      return fetch(theme.routes.cart_url + '.js', { headers: { Accept: 'application/json' } })
        .then(function (res) { return res.json(); })
        .then(syncFrom)
        .catch(function (e) { console.error(e); });
    }

    function sections() {
      return miniCart ? miniCart.getSectionsToRender().map(function (s) { return s.id; }) : [];
    }

    // Same request shapes the theme already uses: add as in AddToCart
    // (global.js), update with the plain JSON config as cart.js does.
    function post(url, body, type) {
      return fetch(url, Object.assign({}, fetchConfig(type || 'javascript'), { body: JSON.stringify(body) }))
        .then(function (res) { return res.json(); })
        .then(function (data) {
          if (data && data.status && data.description) throw new Error(data.description);
          return data;
        });
    }

    // Puts the radios back to the kit that is actually in the cart.
    function restoreSelection() {
      var current = wrap._ogaiCurrent || '';
      var r = radios();
      for (var i = 0; i < r.length; i++) r[i].checked = r[i].value === current;
    }

    function choose(input) {
      var prev = (wrap._ogaiCurrent || '').split(',').filter(Boolean);
      var next = idsOf(input);
      var addIds = next.filter(function (id) { return prev.indexOf(id) === -1; });

      // Shipping-delay confirmation (snippets/ogai-delay-confirm.liquid): a kit
      // that adds a product with the high demand notice asks first. Cancel
      // leaves the cart and the selection as they were.
      var delayIds = (input.getAttribute('data-ogai-delay-variants') || '').split(',').filter(function (id) {
        return id && addIds.indexOf(id) !== -1;
      });
      if (delayIds.length && window.ogaiDelay) {
        setBusy(true);
        window.ogaiDelay.gate(input).then(function (ack) {
          setBusy(false);
          if (ack === null) {
            restoreSelection();
            var sel = selected();
            if (sel) sel.focus();
            return;
          }
          apply(input, ack, delayIds);
        });
        return;
      }
      apply(input, '', []);
    }

    function apply(input, ack, delayIds) {
      var prev = (wrap._ogaiCurrent || '').split(',').filter(Boolean);
      var next = idsOf(input);
      var removeIds = prev.filter(function (id) { return next.indexOf(id) === -1; });
      var addIds = next.filter(function (id) { return prev.indexOf(id) === -1; });
      if (!removeIds.length && !addIds.length) return;
      var prop = window.ogaiDelay ? window.ogaiDelay.PROP : '_Shipping delay acknowledged';

      setBusy(true);
      var chain = Promise.resolve(null);

      if (removeIds.length) {
        chain = chain
          .then(function () {
            return fetch(theme.routes.cart_url + '.js', { headers: { Accept: 'application/json' } }).then(function (res) { return res.json(); });
          })
          .then(function (cart) {
            var q = qtyMap(cart);
            var updates = {};
            removeIds.forEach(function (id) { updates[id] = Math.max((q[id] || 0) - 1, 0); });
            var body = { updates: updates };
            if (!addIds.length) { body.sections = sections(); body.sections_url = window.location.pathname; }
            return post(theme.routes.cart_update_url, body, 'json');
          });
      }

      if (addIds.length) {
        chain = chain.then(function () {
          return post(theme.routes.cart_add_url, {
            items: addIds.map(function (id) {
              var item = { id: Number(id), quantity: 1 };
              if (ack && delayIds.indexOf(id) !== -1) { item.properties = {}; item.properties[prop] = ack; }
              return item;
            }),
            sections: sections(),
            sections_url: window.location.pathname
          });
        });
      }

      chain
        .then(function (response) {
          wrap._ogaiCurrent = input.value;
          if (miniCart && response && response.sections) miniCart.renderContents(response);
          if (typeof publish === 'function' && typeof PUB_SUB_EVENTS !== 'undefined') {
            publish(PUB_SUB_EVENTS.cartUpdate, { source: 'ogai-power-kits', cart: response });
          }
          say(input.value ? (input.getAttribute('data-kit-name') || 'Kit') + ' added to your cart.' : 'Power kit removed from your cart.');
        })
        .catch(function (e) {
          console.error(e);
          say('Sorry, the cart could not be updated. Please try again.');
          return syncFromServer();
        })
        .finally(function () {
          setBusy(false);
          var sel = selected();
          if (sel) sel.focus();
        });
    }

    wrap.addEventListener('change', function (event) {
      var input = event.target;
      if (!input || input.type !== 'radio' || !input.closest('[data-ogai-kits]')) return;
      if (busy) return;
      choose(input);
    });

    // A variant change replaces the block's markup; restore the selection.
    if ('MutationObserver' in window) {
      new MutationObserver(restoreSelection).observe(wrap, { childList: true });
    }

    // Cart changed somewhere else (drawer quantity, remove, Add to cart).
    // Only a whole cart (it carries item_count) is trusted: an /cart/add
    // response also has an items array, but it lists just the lines that
    // were added, and syncing from it loses the current kit — which then
    // re-adds the shared Poseidon XL on the next switch. Anything else is
    // re-read from the server. Events fired while a kit change is in
    // flight are ignored; the change re-syncs when it finishes.
    function onExternalChange(cart) {
      if (busy) return;
      if (cart && cart.items && typeof cart.item_count === 'number') syncFrom(cart);
      else syncFromServer();
    }

    if (typeof subscribe === 'function' && typeof PUB_SUB_EVENTS !== 'undefined') {
      subscribe(PUB_SUB_EVENTS.cartUpdate, function (event) {
        if (event && event.source === 'ogai-power-kits') return;
        onExternalChange(event && event.cart);
      });
    }
    document.addEventListener('cart:updated', function (event) {
      onExternalChange(event.detail && event.detail.cart);
    });

    syncFromServer();
  }

  /* ---- package cards drive the hidden bundle picker ---------------------- */
  /* snippets/ogai-package-cards.liquid renders the Knowledge Bundle choice as
     cards. Choosing one checks the matching radio in the (hidden) option-2
     row of <variant-selects> and fires a bubbling change event, so the
     theme's own variant logic runs exactly as if the button were clicked.
     The cards live in a custom_liquid block that product-info.js re-renders
     on every variant change, so listen on the stable wrapper, not the cards,
     and put focus back on the chosen card after the re-render. */

  function initPackageCards() {
    var first = document.querySelector('[data-ogai-packages]');
    if (!first) return;
    var wrap = first.closest('.custom__liquid') || first.parentNode;
    var refocus = null;

    wrap.addEventListener('change', function (event) {
      var card = event.target;
      if (!card || card.type !== 'radio' || !card.closest('[data-ogai-packages]')) return;
      var fieldset = card.closest('[data-ogai-packages]');
      var name = fieldset.getAttribute('data-input-name');
      var picker = document.querySelector('variant-selects');
      if (!picker) return;
      var inputs = picker.querySelectorAll('input[type="radio"]');
      for (var i = 0; i < inputs.length; i++) {
        // .trim(): the theme's name attribute ends in a newline (see the snippet).
        if (inputs[i].name.trim() === name && inputs[i].value === card.value) {
          if (inputs[i].checked) return;
          inputs[i].checked = true;
          refocus = card.value;
          inputs[i].dispatchEvent(new Event('change', { bubbles: true }));
          return;
        }
      }
    });

    if ('MutationObserver' in window) {
      new MutationObserver(function () {
        if (!refocus) return;
        var cards = wrap.querySelectorAll('[data-ogai-packages] input[type="radio"]');
        for (var i = 0; i < cards.length; i++) {
          if (cards[i].value === refocus) { cards[i].focus(); break; }
        }
        refocus = null;
      }).observe(wrap, { childList: true });
    }
  }

  /* ---- "Compare devices" stays open across variant changes --------------- */
  /* ogai-device-compare renders closed; a variant change re-renders it (see
     above), which would snap it shut under a buyer who opened it. */

  function initCompareDisclosure() {
    var first = document.querySelector('details[data-ogai-device-compare]');
    if (!first) return;
    var wrap = first.closest('.custom__liquid') || first.parentNode;
    var open = false;

    wrap.addEventListener('toggle', function (event) {
      if (event.target.matches && event.target.matches('details[data-ogai-device-compare]')) open = event.target.open;
    }, true);

    if ('MutationObserver' in window) {
      new MutationObserver(function () {
        var d = wrap.querySelector('details[data-ogai-device-compare]');
        if (d && open && !d.open) d.open = true;
      }).observe(wrap, { childList: true });
    }
  }

  onReady(function () {
    initScrollspyReveal();
    initHashReveal();
    initScrollspySync();
    initVariantBoundRows();
    initDeviceCompare();
    initPowerKits();
    initPackageCards();
    initCompareDisclosure();
  });
})();
